import { SharedServicesEnvironment } from '@benefits/shared-services';
import { NgxLoggerLevel } from 'ngx-logger';

export interface Environment extends SharedServicesEnvironment {
  api: string;
  appBaseUrl: string;
  clientId: string;
  consoleTelemetryLevel: NgxLoggerLevel;
  countdownSeconds: number;
  icapEnabled: boolean;
  loginOrigin: string;
  profileSettingsUrl: string;
  partnerPortalUrl: string;
  loginUrl: string;
  pageTitleSuffix: string;
  portalTimeoutMilliseconds: number;
  production?: boolean;
  redirectUrl: string;
  serverTelemetryLevel: NgxLoggerLevel;
  telemetryServerUrl: string;
  claimDetailEmployeeCTAUrl: string;
  claimDetailPolicyHolderCTAUrl: string;
}

export const clientIdDefault = '12u6i5aju4tud969omgbkgaf64'; //Cognito
const baseUrlDefault = 'https://localhost';
const portDefaultString = window.location.port ?? 8080;
const portDefault = parseInt(portDefaultString, 10);
const claimDetailEmployeeCTAUrlDefault = 'https://symetralams--uat.my.salesforce.com/';
const claimDetailPolicyHolderCTAUrlDefault = 'https://symetralams--uat.sandbox.my.site.com/portal/s';

export const setCommonEnvironmentVariables = (baseUrl: string, port: number, clientId: string): Environment => {
  const appBaseUrl = `${baseUrl}:${port}`;
  const loginOrigin = `${baseUrl}:${port}/login`;
  const redirectUrl = `${baseUrl}:${port}/authorize`;
  const profileSettingsUrl = '';
  const partnerPortalUrl = '';

  const environmentValues = {
    api: 'v1',
    appBaseUrl,
    clientId,
    consoleTelemetryLevel: NgxLoggerLevel.DEBUG,
    countdownSeconds: 300,
    icapEnabled: false,
    loginOrigin,
    profileSettingsUrl,
    partnerPortalUrl,
    loginUrl: `https://bdportal-dev.auth.us-west-2.amazoncognito.com/authorize?client_id=${clientId}&response_type=code&scope=email+openid+profile&redirect_uri=${redirectUrl}&identity_provider=NET-IQ`,
    pageTitleSuffix: 'My Benefits at Symetra',
    portalTimeoutMilliseconds: 1500000,
    production: false,
    redirectUrl,
    serverTelemetryLevel: NgxLoggerLevel.OFF,
    telemetryServerUrl: '/tattle/',
    claimDetailEmployeeCTAUrl: claimDetailEmployeeCTAUrlDefault,
    claimDetailPolicyHolderCTAUrl: claimDetailPolicyHolderCTAUrlDefault
  };

  return environmentValues;
};

const commonEnvironmentValues = setCommonEnvironmentVariables(baseUrlDefault, portDefault, clientIdDefault);

export class AppEnvironment implements Environment {
  static empty() {
    return new AppEnvironment({
      api: 'v1',
      appBaseUrl: '',
      clientId: '',
      consoleTelemetryLevel: NgxLoggerLevel.OFF,
      countdownSeconds: 0,
      icapEnabled: false,
      loginOrigin: '',
      loginUrl: '',
      profileSettingsUrl: '',
      partnerPortalUrl: '',
      pageTitleSuffix: '',
      portalTimeoutMilliseconds: 0,
      redirectUrl: '',
      serverTelemetryLevel: NgxLoggerLevel.OFF,
      telemetryServerUrl: '',
      claimDetailEmployeeCTAUrl: '',
      claimDetailPolicyHolderCTAUrl: ''
    });
  }

  api: string;
  appBaseUrl: string;
  clientId: string;
  consoleTelemetryLevel: NgxLoggerLevel;
  countdownSeconds: number;
  icapEnabled: boolean;
  loginOrigin: string;
  profileSettingsUrl: string;
  partnerPortalUrl: string;
  loginUrl: string;
  pageTitleSuffix: string;
  portalTimeoutMilliseconds: number;
  production?: boolean;
  redirectUrl: string;
  serverTelemetryLevel: NgxLoggerLevel;
  telemetryServerUrl: string;
  claimDetailEmployeeCTAUrl: string;
  claimDetailPolicyHolderCTAUrl: string;

  constructor(env: Partial<Environment>) {
    const environment: Environment = {
      ...commonEnvironmentValues,
      ...env
    };

    this.api = environment.api;
    this.appBaseUrl = environment.appBaseUrl;
    this.clientId = environment.clientId;
    this.consoleTelemetryLevel = environment.consoleTelemetryLevel;
    this.countdownSeconds = environment.countdownSeconds;
    this.icapEnabled = environment.icapEnabled;
    this.loginOrigin = environment.loginOrigin;
    this.loginUrl = environment.loginUrl;
    this.profileSettingsUrl = environment.profileSettingsUrl;
    this.partnerPortalUrl = environment.partnerPortalUrl;
    this.pageTitleSuffix = environment.pageTitleSuffix;
    this.portalTimeoutMilliseconds = environment.portalTimeoutMilliseconds;
    this.production = environment.production;
    this.redirectUrl = environment.redirectUrl;
    this.serverTelemetryLevel = environment.serverTelemetryLevel;
    this.telemetryServerUrl = environment.telemetryServerUrl;
    this.claimDetailEmployeeCTAUrl = environment.claimDetailEmployeeCTAUrl;
    this.claimDetailPolicyHolderCTAUrl = environment.claimDetailPolicyHolderCTAUrl;
  }
}

export const commonEnvironment = new AppEnvironment(commonEnvironmentValues);
